import { apiRequest, buildApiUrl, getHeaders } from "../utils/api";

const fetchAnalyticsJson = async (endpoint) => {
  const response = await apiRequest(buildApiUrl(endpoint), {
    headers: getHeaders()
  });

  return response.json();
};

const orgIdCache = {};

const isSystemOrgId = (id) =>
  !id ||
  id === "00000000-0000-0000-0000-000000000000" ||
  id === "00000000-0000-0000-0000-000000000001";

const resolveOrgId = async (organizationId, siteCollectionId) => {
  if (!siteCollectionId) return isSystemOrgId(organizationId) ? "" : organizationId;
  if (orgIdCache[siteCollectionId]) {
    return orgIdCache[siteCollectionId];
  }
  try {
    const response = await apiRequest(buildApiUrl(`/site-collections/${siteCollectionId}`), {
      headers: getHeaders()
    });
    if (response.ok) {
      const data = await response.json();
      if (data?.organizationId) {
        orgIdCache[siteCollectionId] = data.organizationId;
        return data.organizationId;
      }
    }
  } catch (e) {
    console.error("Failed to resolve organizationId for site collection:", e);
  }
  return isSystemOrgId(organizationId) ? "" : organizationId;
};

export const validateDateRange = (fromUtc, toUtc) => {
  if (fromUtc && toUtc) {
    const start = new Date(fromUtc);
    const end = new Date(toUtc);
    if (start >= end) {
      throw new Error("Invalid date range: fromUtc must be earlier than toUtc");
    }
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    // Accommodate 30 calendar days with full day boundaries (e.g. 00:00:00Z to 23:59:59Z is ~30.999 days)
    if (diffDays > 31.05) {
      throw new Error("Invalid date range: window cannot exceed 30 days");
    }
  }
};

export const buildTelemetryQueryParams = ({ siteCollectionId, fromUtc, toUtc, extra = {} } = {}) => {
  validateDateRange(fromUtc, toUtc);
  const params = new URLSearchParams();
  if (siteCollectionId) params.set("siteCollectionId", siteCollectionId);
  if (fromUtc) params.set("fromUtc", fromUtc);
  if (toUtc) params.set("toUtc", toUtc);
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const getAnalyticsSiteCollections = async (params = {}, options = {}) => {
  const organizationId = typeof params === 'string' ? params : params?.organizationId;
  const signal = params?.signal || options?.signal;
  if (!organizationId) return [];

  try {
    const response = await apiRequest(buildApiUrl(`/site-collections?organizationId=${organizationId}`), {
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data)) {
        return data;
      }
    }
  } catch (e) {
    if (e?.name === 'AbortError' || signal?.aborted) {
      return [];
    }
    console.warn(`Failed to fetch site collections for org ${organizationId}:`, e);
  }

  return [];
};

export const getAnalyticsFlowsForSite = async ({ organizationId, siteCollectionId }) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  return fetchAnalyticsJson(`/userflows/site-collections/${siteCollectionId}?organizationId=${resolvedOrgId}`);
};


export const getAnalyticsOrganizations = async () => {
  const response = await apiRequest(buildApiUrl("/organizations"), {
    headers: getHeaders()
  });
  return response.json();
};

/* ================= V1 TELEMETRY QUERY SERVICES ================= */

/**
 * Baseline telemetry usage summary. Available without AdvancedAnalytics entitlement.
 */
export const getUsageSummary = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({ siteCollectionId, fromUtc, toUtc });
  return fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/usage-summary${query}`);
};

/**
 * Active users granularity (DAU, WAU, MAU) and stickiness ratio. Requires AdvancedAnalytics.
 */
export const getActiveUsers = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({ siteCollectionId, fromUtc, toUtc });
  const data = await fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/active-users${query}`);
  const dailyActiveUsers = data?.dailyActiveUsers || 0;
  const weeklyActiveUsers = data?.weeklyActiveUsers || 0;
  const monthlyActiveUsers = data?.monthlyActiveUsers || 0;
  const dauToMauRatio = monthlyActiveUsers > 0
    ? Number(((dailyActiveUsers / monthlyActiveUsers) * 100).toFixed(1))
    : 0;

  return {
    dailyActiveUsers,
    weeklyActiveUsers,
    monthlyActiveUsers,
    dauToMauRatio,
    ...(typeof data === "object" && data !== null ? data : {})
  };
};

/**
 * Session metrics including total count and average duration. Requires AdvancedAnalytics.
 */
export const getSessionMetrics = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({ siteCollectionId, fromUtc, toUtc });
  const data = await fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/session-metrics${query}`);
  return {
    sessionCount: data?.sessionCount || 0,
    averageSessionDurationSeconds: data?.averageSessionDurationSeconds || 0,
    ...(typeof data === "object" && data !== null ? data : {})
  };
};

/**
 * Feature adoption analytics across flows/features. Requires AdvancedAnalytics.
 */
export const getFeatureAdoption = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({ siteCollectionId, fromUtc, toUtc });
  return fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/feature-adoption${query}`);
};

/**
 * Module usage breakdown across IAP modules (e.g. iap_admin, content_studio, player).
 * Requires AdvancedAnalytics.
 */
export const getModuleUsage = async ({ organizationId, siteCollectionId, moduleKey, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({
    siteCollectionId,
    fromUtc,
    toUtc,
    extra: { moduleKey }
  });
  return fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/module-usage${query}`);
};

/**
 * Telemetry pipeline errors and rejected/duplicate ingestion events. Requires AdvancedAnalytics.
 */
export const getTelemetryErrors = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);
  const query = buildTelemetryQueryParams({ siteCollectionId, fromUtc, toUtc });
  return fetchAnalyticsJson(`/telemetry/organizations/${resolvedOrgId}/errors${query}`);
};

/**
 * Detects whether an API error represents an EntitlementDenied 403 response.
 */
export const isEntitlementDenied = (error) => {
  if (!error) return false;
  return (
    error.status === 403 ||
    (typeof error.message === "string" && (
      error.message.includes("EntitlementDenied") ||
      error.message.includes("AdvancedAnalytics") ||
      error.message.includes("403")
    ))
  );
};

/**
 * Aggregates all 6 v1 telemetry query endpoints using Promise.allSettled.
 * Normalizes results into tab-ready structures and catches entitlement denials gracefully.
 */
export const getUnifiedIapAnalytics = async ({ organizationId, siteCollectionId, fromUtc, toUtc } = {}) => {
  validateDateRange(fromUtc, toUtc);
  const params = { organizationId, siteCollectionId, fromUtc, toUtc };

  const [
    usageSummaryResult,
    activeUsersResult,
    sessionMetricsResult,
    featureAdoptionResult,
    moduleUsageResult,
    errorsResult
  ] = await Promise.allSettled([
    getUsageSummary(params),
    getActiveUsers(params),
    getSessionMetrics(params),
    getFeatureAdoption(params),
    getModuleUsage(params),
    getTelemetryErrors(params)
  ]);

  const normalizeSettled = (res) => {
    if (res.status === "fulfilled") {
      return { data: res.value, error: null, isEntitled: true };
    }
    const error = res.reason;
    const isEntitled = !isEntitlementDenied(error);
    return {
      data: null,
      error: error?.message || String(error),
      isEntitled
    };
  };

  const usageSummary = normalizeSettled(usageSummaryResult);
  const activeUsers = normalizeSettled(activeUsersResult);
  const sessionMetrics = normalizeSettled(sessionMetricsResult);
  const featureAdoption = normalizeSettled(featureAdoptionResult);
  const moduleUsage = normalizeSettled(moduleUsageResult);
  const telemetryErrors = normalizeSettled(errorsResult);

  const hasAdvancedAnalytics =
    activeUsers.isEntitled &&
    sessionMetrics.isEntitled &&
    featureAdoption.isEntitled;

  return {
    usageSummary,
    activeUsers,
    sessionMetrics,
    featureAdoption,
    moduleUsage,
    telemetryErrors,
    hasAdvancedAnalytics
  };
};

/**
 * Transforms a CustomerFunnelResponse into the Sankey DAG format expected by @nivo/sankey.
 * @param {object} funnelData - { journeys, steps: [{ actionId, reached, dropped, conversionPercent }] }
 * @param {object} flowDetails - Flow object with { id, flowName, steps: [{ stepId, stepName }] }
 * @returns {{ flowId: string, flowName: string, summary: object, nodes: Array, links: Array }}
 */
export function transformFunnelToSankey(funnelData, flowDetails) {
  const steps = Array.isArray(funnelData?.steps) ? funnelData.steps : [];
  const totalLaunched = Number(funnelData?.journeys ?? (steps[0]?.reached ?? 0));

  const stepMap = new Map();
  if (Array.isArray(flowDetails?.steps)) {
    flowDetails.steps.forEach((s, idx) => {
      const id = typeof s === 'string' ? s : String(s?.actionId || s?.stepId || s?.id);
      const name = typeof s === 'object' && s !== null ? (s?.stepName || s?.name) : `Step ${idx + 1}`;
      if (id) stepMap.set(id, name || `Step ${idx + 1}`);
    });
  }

  if (steps.length === 0) {
    return {
      flowId: flowDetails?.id,
      flowName: flowDetails?.flowName || flowDetails?.name,
      summary: {
        totalLaunched: 0,
        totalCompleted: 0,
        totalExited: 0,
        overallCompletionRate: 0
      },
      nodes: [],
      links: []
    };
  }

  const nodes = [];
  const links = [];

  // 1. Entry node
  nodes.push({
    id: 'entry',
    label: 'Flow Started',
    type: 'entry',
    count: totalLaunched,
    completionRate: 100,
    exitCount: 0
  });

  let previousNodeId = 'entry';
  const shouldConsolidateExits = steps.length > 8;
  let totalDroppedCount = 0;
  if (shouldConsolidateExits) {
    steps.forEach((step) => {
      totalDroppedCount += Number(step.dropped || 0);
    });
  }

  steps.forEach((step, index) => {
    const stepId = String(step.actionId || `step_${index + 1}`);
    const stepLabel = stepMap.get(stepId) || `Step ${index + 1}`;
    const exitId = `exit_${stepId}`;
    const isLast = index === steps.length - 1;
    const reached = Number(step.reached || 0);
    const dropped = Number(step.dropped || 0);
    const conversionPercent = step.conversionPercent !== undefined && step.conversionPercent !== null
      ? Number(step.conversionPercent)
      : null;

    // Step Node
    nodes.push({
      id: stepId,
      label: stepLabel,
      type: 'step',
      stepOrder: index + 1,
      count: reached,
      completionRate: conversionPercent,
      exitCount: dropped
    });

    // Link from previous step to this step
    if (reached > 0) {
      links.push({
        source: previousNodeId,
        target: stepId,
        value: reached
      });
    }

    // Drop-off / Exit link & node if any users dropped
    if (dropped > 0) {
      if (shouldConsolidateExits) {
        links.push({
          source: stepId,
          target: 'exit_dropoff',
          value: dropped
        });
      } else {
        nodes.push({
          id: exitId,
          label: `Drop-off (${stepLabel})`,
          type: 'exit',
          count: dropped,
          completionRate: null,
          exitCount: dropped
        });

        links.push({
          source: stepId,
          target: exitId,
          value: dropped
        });
      }
    }

    // Terminal completion link from the last step
    if (isLast) {
      const completedCount = Math.max(0, reached - dropped);
      nodes.push({
        id: 'completed',
        label: 'Completed Flow',
        type: 'terminal',
        count: completedCount,
        completionRate: conversionPercent,
        exitCount: 0
      });

      if (completedCount > 0) {
        links.push({
          source: stepId,
          target: 'completed',
          value: completedCount
        });
      }
    }

    previousNodeId = stepId;
  });

  if (shouldConsolidateExits && totalDroppedCount > 0) {
    nodes.push({
      id: 'exit_dropoff',
      label: 'Flow Drop-offs',
      type: 'exit',
      count: totalDroppedCount,
      completionRate: null,
      exitCount: totalDroppedCount
    });
  }

  const lastStep = steps[steps.length - 1];
  const lastReached = Number(lastStep?.reached || 0);
  const lastDropped = Number(lastStep?.dropped || 0);
  const totalCompleted = Math.max(0, lastReached - lastDropped);
  const totalExited = Math.max(0, totalLaunched - totalCompleted);

  return {
    flowId: flowDetails?.id,
    flowName: flowDetails?.flowName || flowDetails?.name,
    summary: {
      totalLaunched,
      totalCompleted,
      totalExited,
      overallCompletionRate: totalLaunched > 0 ? Number(((totalCompleted / totalLaunched) * 100).toFixed(1)) : 0
    },
    nodes,
    links
  };
}

/**
 * Sankey progression journey data for a specific guided flow (or portfolio overview).
 * Queries the v1 Flow Sankey Telemetry API:
 * GET /api/v1/telemetry/organizations/{orgId}/site-collections/{siteId}/flows/sankey
 * Pre-formatted for @nivo/sankey with DAG nodes, links, and KPI summary.
 * Requires AdvancedAnalytics entitlement.
 */
export const getFlowSankey = async ({
  organizationId,
  siteCollectionId,
  flowId,
  userFlowId,
  flow,
  viewType,
  fromUtc,
  toUtc,
  startDate,
  endDate,
  deviceType,
  browser
} = {}) => {
  const targetFlowId = flowId || userFlowId || flow?.id;
  const start = startDate || fromUtc;
  const end = endDate || toUtc;

  validateDateRange(start, end);
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);

  const params = new URLSearchParams();
  if (start) params.set("startDate", start);
  if (end) params.set("endDate", end);
  if (targetFlowId) params.set("flowId", targetFlowId);
  if (viewType) {
    params.set("viewType", viewType);
  } else if (targetFlowId) {
    params.set("viewType", "single_flow");
  }
  if (deviceType && deviceType !== "all") params.set("deviceType", deviceType);
  if (browser && browser !== "all") params.set("browser", browser);

  const qs = params.toString() ? `?${params.toString()}` : "";
  const endpoint = `/telemetry/organizations/${encodeURIComponent(resolvedOrgId)}/site-collections/${encodeURIComponent(siteCollectionId)}/flows/sankey${qs}`;

  const data = await fetchAnalyticsJson(endpoint);

  return {
    ...data,
    summary: data?.summary || {
      totalLaunched: 0,
      totalCompleted: 0,
      totalExited: 0,
      overallCompletionRate: 0
    },
    nodes: Array.isArray(data?.nodes) ? data.nodes : [],
    links: Array.isArray(data?.links) ? data.links : []
  };
};


export const getFlowEnvironment = async ({
  organizationId,
  siteCollectionId,
  startDate,
  endDate,
  fromUtc,
  toUtc
} = {}) => {
  let start = fromUtc || startDate;
  let end = toUtc || endDate;

  if (start && typeof start === 'string' && !start.includes('T')) {
    start = `${start}T00:00:00Z`;
  }
  if (end && typeof end === 'string' && !end.includes('T')) {
    end = `${end}T23:59:59Z`;
  }

  if (start && end && new Date(start).getTime() >= new Date(end).getTime()) {
    const d = new Date(start);
    d.setUTCHours(23, 59, 59, 999);
    end = d.toISOString();
  }

  validateDateRange(start, end);
  const resolvedOrgId = await resolveOrgId(organizationId, siteCollectionId);

  const params = new URLSearchParams();
  if (start) params.set('StartDate', start);
  if (end) params.set('EndDate', end);

  const qs = params.toString() ? `?${params.toString()}` : '';
  const endpoint = `/telemetry/organizations/${encodeURIComponent(resolvedOrgId)}/site-collections/${encodeURIComponent(siteCollectionId)}/flows/environment${qs}`;

  const data = await fetchAnalyticsJson(endpoint);

  return {
    siteCollectionId: data?.siteCollectionId || siteCollectionId,
    flowId: data?.flowId || null,
    dateRange: data?.dateRange || { startDate: start, endDate: end },
    totalSessions: Number(data?.totalSessions ?? 0),
    deviceTypes: Array.isArray(data?.deviceTypes) ? data.deviceTypes : [],
    browsers: Array.isArray(data?.browsers) ? data.browsers : [],
    operatingSystems: Array.isArray(data?.operatingSystems) ? data.operatingSystems : [],
    locations: Array.isArray(data?.locations) ? data.locations : []
  };
};
